import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Spinner } from "react-bootstrap";
import { userLoggedIn, userLoggedOut } from "app/redux/auth/authSlice";
import { clearAuthentication, logoutSession } from "app/services/apiClient";

const PROMPT_BEFORE_EXPIRY_MS = 2 * 60 * 1000;

export default function SessionExpiryManager() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const promptTimer = useRef(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(() =>
    localStorage.getItem("tokenExpiresAt")
  );
  const [showPrompt, setShowPrompt] = useState(false);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    if (promptTimer.current) clearTimeout(promptTimer.current);
    if (!tokenExpiresAt || localStorage.getItem("sessionActive") !== "true") return undefined;

    const expiresAt = new Date(tokenExpiresAt).getTime();
    if (!Number.isFinite(expiresAt)) return undefined;

    const promptAt = expiresAt - PROMPT_BEFORE_EXPIRY_MS;
    const delay = Math.max(promptAt - Date.now(), 0);

    promptTimer.current = window.setTimeout(() => {
      setShowPrompt(true);
    }, delay);

    return () => {
      if (promptTimer.current) clearTimeout(promptTimer.current);
    };
  }, [tokenExpiresAt]);

  useEffect(() => {
    const syncSessionAcrossTabs = (event) => {
      if (event.key === "tokenExpiresAt") {
        setTokenExpiresAt(event.newValue);
        setShowPrompt(false);
      } else if (event.key === "sessionActive" && !event.newValue) {
        setShowPrompt(false);
      }
    };

    window.addEventListener("storage", syncSessionAcrossTabs);
    return () => window.removeEventListener("storage", syncSessionAcrossTabs);
  }, []);

  const logout = async () => {
    try {
      await logoutSession();
    } finally {
      clearAuthentication();
      dispatch(userLoggedOut());
      sessionStorage.setItem("authRedirectReason", "session-expired");
      window.location.replace("/sessions/signin");
    }
  };

  const renewSession = async () => {
    if (localStorage.getItem("sessionActive") !== "true") {
      logout();
      return;
    }

    setRenewing(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/user/renew-session`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.tokenExpiresAt) {
        logout();
        return;
      }

      localStorage.setItem("tokenExpiresAt", data.tokenExpiresAt);
      dispatch(userLoggedIn({ user }));
      setTokenExpiresAt(data.tokenExpiresAt);
      setShowPrompt(false);
    } catch {
      logout();
    } finally {
      setRenewing(false);
    }
  };

  const letSessionExpire = () => {
    setShowPrompt(false);
  };

  return (
    <Modal
      show={showPrompt}
      onHide={letSessionExpire}
      centered
      backdrop={renewing ? "static" : true}
      keyboard={!renewing}
    >
      <Modal.Header closeButton={!renewing}>
        <Modal.Title>Session expiring soon</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">Session expires in 2 mins. Do you want to continue?</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={renewing} onClick={letSessionExpire}>
          No
        </Button>
        <Button disabled={renewing} onClick={renewSession}>
          {renewing && <Spinner animation="border" size="sm" className="me-2" />}
          {renewing ? "Continuing..." : "Yes, continue"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
